import { useState, useRef, useEffect } from 'react'
import { useMediaModal } from '../../context/MediaModalContext'
import { saveMessageMedia } from '../../services/mediaService'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

/**
 * Single shared media modal component
 * Uses Tailwind CSS for styling and React Context for state management
 * Replaces multiple Bootstrap modals with one performant instance
 */
export const MediaModal = () => {
  const {
    isOpen,
    currentMedia,
    allMedia,
    currentIndex,
    closeModal,
    navigateNext,
    navigatePrevious,
    navigateToMedia
  } = useMediaModal()

  const { t } = useTranslation()

  // Zoom state for images
  const [isZoomed, setIsZoomed] = useState(false)
  const [zoomPosition, setZoomPosition] = useState({ x: 0, y: 0 })

  // Refs for focus management
  const modalRef = useRef<HTMLDivElement>(null)
  const previousFocusRef = useRef<HTMLElement | null>(null)

  /**
   * Focus management - trap focus in modal and restore on close
   */
  useEffect(() => {
    if (isOpen) {
      // Save current focus
      previousFocusRef.current = document.activeElement as HTMLElement
      // Focus modal container
      modalRef.current?.focus()
    } else {
      // Restore previous focus
      previousFocusRef.current?.focus()
    }
  }, [isOpen])

  /**
   * Reset zoom when media changes
   */
  useEffect(() => {
    setIsZoomed(false)
    setZoomPosition({ x: 0, y: 0 })
  }, [currentMedia?.id])

  /**
   * Handle backdrop click to close modal
   * Only closes if clicking backdrop itself, not content
   */
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      closeModal()
    }
  }

  /**
   * Handle image click for zoom functionality
   * Calculates zoom position based on click location
   */
  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    if (!isZoomed) {
      const img = e.target as HTMLImageElement
      const rect = img.getBoundingClientRect()
      const x = e.clientX - rect.left
      const y = e.clientY - rect.top
      const centerX = rect.width / 2
      const centerY = rect.height / 2

      // Calculate zoom position (move image opposite to click position)
      const zoomX = (centerX - x) * 0.5
      const zoomY = (centerY - y) * 0.5

      setZoomPosition({ x: zoomX, y: zoomY })
      setIsZoomed(true)
    } else {
      setIsZoomed(false)
      setZoomPosition({ x: 0, y: 0 })
    }
  }

  /**
   * Handle download button click
   * Creates a proper message object for mediaService with all available metadata
   */
  const handleDownload = () => {
    try {
      console.log('[MediaModal] Download initiated:', currentMedia)

      // Construct message-like object with all available metadata
      // This supports both new and legacy formats in mediaService
      // Filename priority: filename > fileRecordId > name > "Untitled"
      const messageForDownload = {
        // New format (preferred)
        media: {
          url: currentMedia.url,
          filename: currentMedia.filename,
          fileRecordId: currentMedia.fileRecordId,
          mimeType: currentMedia.mimeType,
          extension: currentMedia.extension,
          size: currentMedia.size,
          duration: currentMedia.duration
        },
        // Legacy format (fallback)
        mediaUrl: currentMedia.url,
        filename: currentMedia.filename || currentMedia.name,
        fileId: currentMedia.fileRecordId,
        duration: currentMedia.duration
      } as any  // Type assertion: mediaService accepts partial message objects

      // Use existing mediaService which properly handles:
      // 1. Both new and legacy formats
      // 2. Extension extraction from mimeType
      // 3. Extension extraction from URL
      // 4. FileSaver.js for better cross-origin handling
      saveMessageMedia(messageForDownload)

      console.log('[MediaModal] Download triggered via mediaService')
    } catch (error) {
      console.error('[MediaModal] Download failed:', error)
      // Final fallback: open in new tab
      window.open(currentMedia.url, '_blank')
    }
  }

  // Navigation availability
  const hasPrevious = currentIndex > 0
  const hasNext = currentIndex < allMedia.length - 1
  const showThumbnails = allMedia.length > 1

  // Don't render if not open
  if (!isOpen || !currentMedia) return null

  return (
    <div
      ref={modalRef}
      tabIndex={-1}
      role="dialog"
      aria-modal="true"
      aria-label={t('HC.File.MediaViewer') || 'Media viewer'}
      className="tw-fixed tw-inset-0 tw-bg-black tw-bg-opacity-95 tw-overflow-hidden"
      style={{ zIndex: 9999 }}
      onClick={handleBackdropClick}
    >
      {/* Header Bar with Close and Download buttons */}
      <div
        className="tw-absolute tw-top-0 tw-left-0 tw-right-0 tw-flex tw-justify-between tw-items-center tw-p-4"
        style={{
          background: 'linear-gradient(180deg, rgba(0,0,0,0.7) 0%, transparent 100%)',
          zIndex: 10
        }}
      >
        {/* Close Button */}
        <button
          id="media-modal-close-btn"
          data-testid="media-modal-close-btn"
          className="tw-w-14 tw-h-14 tw-rounded-full tw-bg-white tw-bg-opacity-20 tw-border-2 tw-border-white tw-border-opacity-50 tw-text-white tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-transition-all tw-duration-300 hover:tw-bg-opacity-40 hover:tw-scale-110 tw-shadow-lg"
          onClick={closeModal}
          aria-label={t('HC.Button.Close') || 'Close'}
        >
          <i className="bi bi-x-lg tw-text-2xl" aria-hidden="true"></i>
        </button>

        {/* Download Button */}
        <button
          id="media-modal-download-btn"
          data-testid="media-modal-download-btn"
          className="tw-w-14 tw-h-14 tw-rounded-full tw-bg-white tw-bg-opacity-20 tw-border-2 tw-border-white tw-border-opacity-50 tw-text-white tw-flex tw-items-center tw-justify-center tw-cursor-pointer tw-transition-all tw-duration-300 hover:tw-bg-opacity-40 hover:tw-scale-110 tw-shadow-lg"
          onClick={handleDownload}
          aria-label={t('HC.Button.Download') || 'Download'}
        >
          <i className="bi bi-download tw-text-xl" aria-hidden="true"></i>
        </button>
      </div>

      {/* Main Content Area */}
      <div className="tw-flex-grow tw-relative tw-flex tw-items-center tw-justify-center tw-w-full tw-h-full">
        {/* Previous Navigation Button */}
        {hasPrevious && (
          <button
            id="media-modal-prev-btn"
            data-testid="media-modal-prev-btn"
            className="tw-absolute tw-left-6 tw-top-1/2 tw--translate-y-1/2 tw-z-15 tw-w-15 tw-h-15 tw-rounded-full tw-bg-black tw-bg-opacity-40 tw-border tw-border-white tw-border-opacity-10 tw-text-white tw-opacity-70 hover:tw-opacity-100 hover:tw-bg-opacity-60 hover:tw-scale-110 tw-transition-all tw-duration-300 tw-flex tw-items-center tw-justify-center tw-cursor-pointer"
            onClick={navigatePrevious}
            aria-label={t('HC.Button.Previous') || 'Previous media'}
          >
            <i className="bi bi-chevron-left" aria-hidden="true"></i>
          </button>
        )}

        {/* Media Content Container */}
        <div
          className="tw-flex tw-items-center tw-justify-center tw-w-full"
          style={{
            padding: '80px 80px 150px 80px',
            maxHeight: 'calc(100vh - 80px - 150px)',
            overflow: isZoomed ? 'auto' : 'hidden'
          }}
        >
          {/* Image / Sticker Rendering */}
          {(currentMedia.type === 'image' ||
            currentMedia.type === 'image_with_text' ||
            currentMedia.type === 'sticker') && (
            <img
              src={currentMedia.url}
              alt={currentMedia.name || 'Media'}
              onClick={handleImageClick}
              className={clsx(
                'tw-block tw-transition-all tw-duration-300 tw-ease-in-out tw-object-contain',
                isZoomed
                  ? 'tw-w-[400%] tw-h-[400%] tw-max-w-none tw-max-h-none tw-cursor-zoom-out'
                  : 'tw-max-w-[calc(100vw-200px)] tw-max-h-[calc(100vh-230px)] tw-w-auto tw-h-auto tw-cursor-zoom-in'
              )}
              style={
                isZoomed
                  ? {
                      transform: `translate(${zoomPosition.x}px, ${zoomPosition.y}px)`,
                      transformOrigin: 'center center'
                    }
                  : {}
              }
              onLoad={(e) => {
                if (!isZoomed) {
                  // Ensure image maintains its natural aspect ratio when not zoomed
                  const img = e.target as HTMLImageElement
                  const naturalRatio = img.naturalWidth / img.naturalHeight
                  const maxWidth = window.innerWidth - 200
                  const maxHeight = window.innerHeight - 230
                  const containerRatio = maxWidth / maxHeight

                  if (naturalRatio > containerRatio) {
                    // Image is wider, fit to width
                    img.style.width = Math.min(img.naturalWidth, maxWidth) + 'px'
                    img.style.height = 'auto'
                  } else {
                    // Image is taller, fit to height
                    img.style.height = Math.min(img.naturalHeight, maxHeight) + 'px'
                    img.style.width = 'auto'
                  }
                }
              }}
            />
          )}

          {/* Video Rendering */}
          {currentMedia.type === 'video' && (
            <video
              src={currentMedia.url}
              controls
              autoPlay={false}
              preload="metadata"
              className="tw-max-w-[calc(100vw-200px)] tw-max-h-[calc(100vh-230px)] tw-w-auto tw-h-auto tw-object-contain"
              onLoadedMetadata={(e) => {
                // Ensure video maintains its natural aspect ratio
                const video = e.target as HTMLVideoElement
                const naturalRatio = video.videoWidth / video.videoHeight
                const maxWidth = window.innerWidth - 200
                const maxHeight = window.innerHeight - 230
                const containerRatio = maxWidth / maxHeight

                if (naturalRatio > containerRatio) {
                  // Video is wider, fit to width
                  video.style.width = Math.min(video.videoWidth, maxWidth) + 'px'
                  video.style.height = 'auto'
                } else {
                  // Video is taller, fit to height
                  video.style.height = Math.min(video.videoHeight, maxHeight) + 'px'
                  video.style.width = 'auto'
                }
              }}
            />
          )}
        </div>

        {/* Next Navigation Button */}
        {hasNext && (
          <button
            id="media-modal-next-btn"
            data-testid="media-modal-next-btn"
            className="tw-absolute tw-right-6 tw-top-1/2 tw--translate-y-1/2 tw-z-15 tw-w-15 tw-h-15 tw-rounded-full tw-bg-black tw-bg-opacity-40 tw-border tw-border-white tw-border-opacity-10 tw-text-white tw-opacity-70 hover:tw-opacity-100 hover:tw-bg-opacity-60 hover:tw-scale-110 tw-transition-all tw-duration-300 tw-flex tw-items-center tw-justify-center tw-cursor-pointer"
            onClick={navigateNext}
            aria-label={t('HC.Button.Next') || 'Next media'}
          >
            <i className="bi bi-chevron-right" aria-hidden="true"></i>
          </button>
        )}
      </div>

      {/* Thumbnail Strip */}
      {showThumbnails && (
        <div
          className="tw-absolute tw-bottom-0 tw-left-0 tw-right-0 tw-z-20 tw-flex tw-justify-center tw-p-3"
          style={{
            background: 'linear-gradient(0deg, rgba(0,0,0,0.8) 0%, transparent 100%)'
          }}
        >
          <div
            className="tw-flex tw-gap-2 tw-overflow-x-auto"
            style={{
              maxWidth: '100%',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none'
            }}
          >
            {allMedia.map((media) => {
              const isActive = media.id === currentMedia.id
              return (
                <button
                  key={media.id}
                  type="button"
                  data-testid={`media-modal-thumbnail-${media.id}`}
                  className={clsx(
                    'tw-relative tw-w-16 tw-h-16 tw-rounded-xl tw-border-none tw-overflow-hidden tw-flex-shrink-0 tw-cursor-pointer tw-transition-all tw-duration-300 tw-bg-transparent tw-shadow-lg',
                    isActive
                      ? 'tw-opacity-100 tw-ring-3 tw-ring-white tw-ring-opacity-80 tw-scale-105'
                      : 'tw-opacity-60 hover:tw-opacity-90 hover:tw-scale-105 hover:tw--translate-y-0.5 tw-ring-2 tw-ring-white tw-ring-opacity-10'
                  )}
                  onClick={(e) => {
                    e.stopPropagation()
                    navigateToMedia(media.id)
                  }}
                  aria-label={`${t('HC.Button.View') || 'View'} ${media.type}`}
                >
                  {media.type === 'video' ? (
                    <div className="tw-relative tw-w-full tw-h-full">
                      <img
                        src={media.thumbnail || media.url}
                        alt=""
                        className="tw-w-full tw-h-full tw-object-cover"
                      />
                      <div className="tw-absolute tw-top-1/2 tw-left-1/2 tw--translate-x-1/2 tw--translate-y-1/2 tw-text-white tw-text-base">
                        <i className="bi bi-play-fill" aria-hidden="true"></i>
                      </div>
                    </div>
                  ) : (
                    <img
                      src={media.thumbnail || media.url}
                      alt=""
                      className="tw-w-full tw-h-full tw-object-cover"
                    />
                  )}
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
